import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, Inject } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { SaveModuleConfigModel } from 'src/app/modules/models/save-module-config.model';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { SaveModule, ResetModules, GetModuleSecondaryCategories, SaveModuleSuccess, SaveModuleFailure } from 'src/app/modules/state/modules.actions';
import { ModulesState } from 'src/app/modules/state/modules.state';
import { Observable, Subject, combineLatest, of, merge } from 'rxjs';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl } from '@angular/forms';
import { takeUntil, debounceTime, map, switchMap, zip, mergeAll, combineAll, tap, finalize, distinctUntilChanged } from 'rxjs/operators';
import { SaveModuleModel } from 'src/app/modules/models/save-module.model';
import { ModulesService } from 'src/app/modules/modules.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface SaveModuleDialogData {
  id: string;
}

interface ValidatedState {
  stateControl: AbstractControl; 
  valid: boolean;
}

const validString = '[0-9a-zA-Z_]*';
const validRegex = /^[0-9a-zA-Z_]+$/;

@Component({
  selector: 'app-save-module-dialog',
  templateUrl: './save-module-dialog.component.html',
  styleUrls: ['./save-module-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaveModuleDialogComponent implements OnInit, OnDestroy {
  @Select(ModulesState.getSaveModule)
  saveModule$: Observable<SaveModuleModel>;

  @Select(ModulesState.getPrimaryCategoryOptions('write'))
  primaryCategoryOptions$: Observable<string[]>;

  @Select(ModulesState.getSecondaryCategoryOptions)
  secondaryCategoryOptions$: Observable<string[]>;

  @Select(ModulesState.getSaving)
  saving$: Observable<boolean>;

  moduleExists = false;
  modulePath: string;
  form: FormGroup;
  saveModule: SaveModuleModel;
  usedStates: string[];
  validating = false;

  ngUnsubscribe = new Subject();

  settings: SaveModuleDialogData;

  get formName() {
    return this.form.get('name');
  }
  
  get formStates() {
    return this.form.get('states') as FormArray;
  }

  get formPrimaryCategory() {
    return this.form.get('primaryCategory');
  }

  get formSecondaryCategory() {
    return this.form.get('secondaryCategory');
  }

  get formDuplicateStates() {
    return this.form.get('duplicateStates');
  }

  get formTrimGraph() {
    return this.form.get('trimGraph');
  }

  get formConfirmProduction() {
    return this.form.get('confirmProduction');
  }


  constructor(
    private store: Store,
    private actions: Actions,
    private formBuilder: FormBuilder,
    private modulesService: ModulesService,
    private changeDetector: ChangeDetectorRef,
    private snackbar: MatSnackBar,
    public dialogRef: MatDialogRef<SaveModuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SaveModuleDialogData
  ) {
    this.settings = {...data};
  }
  
  ngOnInit() {
    this.store.dispatch(new ResetModules());

    this.saveModule = this.generateSaveModule();
    this.usedStates = this.modulesService.getUsedStates();
   
    this.initApplicationListeners();
    this.initForm();
    this.setFormValidators();
    this.initFormListeners();
    this.setFormValues(this.saveModule);
    
    this.populateStateValues();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initApplicationListeners() {
    this.actions
      .pipe(
        ofActionDispatched(SaveModuleSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: SaveModuleSuccess) => {
        this.handleSaveSuccess(action.id);
      });
    this.actions
      .pipe(
        ofActionDispatched(SaveModuleFailure),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: SaveModuleFailure) => {
        this.handleSaveFail(action.id);
      });
  }

  initFormListeners() {
    this.formPrimaryCategory.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        this.store.dispatch(new GetModuleSecondaryCategories(value));
      });

    // Listens to the name field to check for a previously existing file. This
    // indicates whether you're saving a new file, or saving over on that exists.
    combineLatest(
      this.formPrimaryCategory.valueChanges,
      this.formSecondaryCategory.valueChanges,
      this.formName.valueChanges,
      this.formDuplicateStates.valueChanges
    )
    .pipe(
      takeUntil(this.ngUnsubscribe),
      debounceTime(300)
    )
    .subscribe(([primaryCategory, secondaryCategory, name, duplicateStates]) => {
      this.handleNameChange(primaryCategory, secondaryCategory, name, duplicateStates);
    });

    // Listens to the state fields to check for previous existing states. States cannot be overridden,
    // so the user is warned when there is a matc and saving is blocked. 
    merge(
      this.formDuplicateStates.valueChanges,
      this.formStates.valueChanges
    )
    .pipe(
      tap(() => this.validating = true),
      debounceTime(300),
      switchMap((values) => {
        return this.checkStateGroups();
      }),
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe((validatedState) => {
      this.validating = false;
      this.updateFormStates(validatedState);
    });
  }

  initForm() {
    this.form = this.formBuilder.group({
      primaryCategory: ['', Validators.required],
      secondaryCategory: [''],
      name: ['', [Validators.required, Validators.pattern(validString)]],
      trimGraph: [false],
      duplicateStates: [false],
      confirmProduction: [false, this.validateProductionSave],
      states: this.formBuilder.array([
        this.initStateForm('')
      ])
    }, { 
      validators: [this.validateProductionSave]
    });
  }

  initStateForm(state) {
    return this.formBuilder.group({
      include: [true],
      oldState: [state],
      newState: [state, [Validators.required, Validators.pattern(validString)]]
    });
  }

  handleReplaceClick(value: string) {
    this.changeStatePrefix(value, 'replace');
  }
 
  handlePrependClick(value: string) {
    this.changeStatePrefix(value, 'prepend');
  }

  handleSubmit() {
    if (this.form.valid) {
      this.save();
    }
  }

  handleSaveSuccess(id: string) {
    this.dialogRef.close();
    this.snackbar.open(`"${id}" successfully saved.`);
  }

  handleSaveFail(id: string) {
    this.snackbar.open(`There was an error saving "${id}".`);
  }

  // Generate a save module model from the passed id
  generateSaveModule() {
    const module = this.modulesService.generateSaveModule(this.settings.id);
    
    return module;
  }

  populateStateValues() {
    // Clear out previous states, rebind with new values
    while (this.formStates.length > 0) {
      this.formStates.removeAt(0);
    }

    const states = [];
    this.modulesService.getUsedStates()
      .forEach(state => {
        const stateControl = { enabled: true, state: state };
        this.formStates.push(this.initStateForm(stateControl.state));
        states.push(stateControl);
      });
    
    this.formStates.markAsDirty();
    // this.handleStatesChange(states);
  }

  setFormValidators() {
    // Secondary category is only required when primary category is production or client.
    // In all other cases, we hide the secondary category.
    this.formPrimaryCategory.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        switch (value) {
          case 'production':
          case 'client':
            this.formSecondaryCategory.setValidators([Validators.required]);
            break;
          default:
           this.formSecondaryCategory.setValidators(null);
           this.formSecondaryCategory.setValue('');
           break;
        }
      });
  }

  setFormValues(saveModule: SaveModuleModel) {
    // Never allow primary cateogry to save to the exports folder.
    // Exports is the single source of truth: models which have been exported from source model files,
    // and therefore should never be allowed to be overwritten.
    const category = (saveModule.primaryCategory === 'exports') ? 'preview' : saveModule.primaryCategory || 'preview';

    this.form.patchValue({
      primaryCategory: category,
      secondaryCategory: saveModule.secondaryCategory || '',
      name: saveModule.name || '',
      trimGraph: false,
      duplicateStates: false
    });
  }

  save() {
    // Generate the save path
    const path = this.modulesService.generateModulePath(
      this.formPrimaryCategory.value,
      this.formSecondaryCategory.value,
      this.formName.value
    );

    // Generate the save configuration (save or duplicate)
    const saveConfig = new SaveModuleConfigModel();
    if (this.formDuplicateStates.value) {
      saveConfig.duplicate = true;

      // If we're duplicating the states, generate a new list of states for copying
      this.formStates.value.map((state: {include: Boolean, oldState: string, newState: string }) => {
        if (state.include) {
          saveConfig.namespaceMap[state.oldState] = state.newState;  
        }
      });
    }
    
    // Generate the trim configuration. Currently only trimming visible is supported.
    const trimConfig = new TrimConfiguration();
    trimConfig.graph.hidden = this.formTrimGraph.value;

    // Send everything off for saving.
    this.store.dispatch(new SaveModule(path, saveConfig, trimConfig));
  }

  handleNameChange(primaryCategory: string, secondaryCategory: string, name, duplicateStates: boolean) {
    this.modulePath = this.modulesService.generateModulePath(primaryCategory, secondaryCategory, name);

    if (this.formName.errors) {
      const { pattern, required } = this.formName.errors;
      if (pattern || required) {
        return;
      }
    }

    if (this.modulePath && name.length) {
      this.modulesService.listContents(primaryCategory, secondaryCategory, name)
        .subscribe((results) => {
          // When duplicating states, name is required
          this.moduleExists = results.length > 0;
          const nameTaken = duplicateStates && this.moduleExists;

          // Ensure valid name after async operation
          const invalid = !validRegex.test(name);

          if (nameTaken || invalid) {
            this.formName.setErrors({
              invalid: true
            });
          } else {
            this.formName.setErrors(null);
          }

          this.formName.markAsDirty();
          this.changeDetector.markForCheck();
        });
    }
  }

  validateProductionSave(form: FormGroup) {
    const primaryCategory = form.get('primaryCategory');
    const confirmProduction = form.get('confirmProduction');

    if (!primaryCategory || !confirmProduction) {
      return null;
    }
    
    if (primaryCategory.value === 'production' && confirmProduction.value === false) {
      return { confirmProduction: true };
    }
    return null;
  }

  // Generates an array of server state name checks using the form controls
  checkStateGroups(): Observable<ValidatedState[]> {
    const groups$ = this.formStates.controls.map(group => {
      return this.validateStateGroup(group);
    });
    
    return groups$.length > 0 ? combineLatest(...groups$) : of([]);
  }

  validateStateGroup(stateGroup: AbstractControl): Observable<ValidatedState> {
    const validateStates = this.formDuplicateStates.value === true;
    const stateControl = stateGroup.get('newState');
    const includeControl = stateGroup.get('include');

    // If state control isn't toggled, auto validate to true
    if (!validateStates || includeControl.value === false) {
      return of({
        stateControl: stateControl,
        valid: true
      });
    }
    
    // If state control fails local validation, fail immediately without further checks
    if (stateControl.errors || stateControl.value.length === 0) {
      return of({
        stateControl: stateControl,
        valid: false
      });
    }

    // If all local checks pass, check server for availability
    return this.modulesService.getState(stateControl.value)
      .pipe(
        map((result) => {
          const isValidString = validRegex.test(stateControl.value);

          return {
            stateControl: stateControl,
            valid: result.length === 0 && isValidString
          };
        })
      );
  }

  // Updates the form validation based on server validation checks
  updateFormStates(validatedStates: ValidatedState[]) {
    validatedStates.forEach((validatedState) => {
      if (validatedState.valid) {
        validatedState.stateControl.setErrors(null);
        validatedState.stateControl.markAsDirty();
      } else {
        validatedState.stateControl.setErrors({ stateTaken: true });
      }
    });

    this.changeDetector.markForCheck();
  }

  changeStatePrefix(newPrefix: string, mode: 'prepend'|'replace') {
    this.formStates.controls.forEach(group => {
      const stateControl = group.get('newState');

      switch (mode) {
        case 'prepend':
          stateControl.setValue(`${newPrefix}_${stateControl.value}`);
          break;
        case 'replace':
          const regex = new RegExp(/[^_]*/); // Match everything before underscore
          const value = stateControl.value.replace(regex, newPrefix);
          stateControl.setValue(value);
          break;
      }
    });
  }
}
